import { useCallback, useState } from "react";

import { Button, Callout, Drawer, Field, Icons, TextArea } from "../../../ds";
import { ILockService, lockErrorKind, lockErrorMessage, lockService } from "../services/LockService";
import { IdentityUnavailableText } from "./LockStates";
import { CannotLock, LockNote, LockWarning } from "./lockTexts";

interface ILockPageDrawerProps {
    open: boolean;
    spaceId: string;
    pageId: string;
    /** Injected in tests; the shared facade client otherwise. */
    service?: ILockService;
    onClose: () => void;
    /** The lock went through and the page is still visible to the caller (refresh the badges and the lock panel). */
    onLocked: () => void;
    /** The lock went through and hid the page from the caller (not one of its readers yet). */
    onHidden: () => void;
}

/**
 * "Lock this page" (OrbitalPlan/18 D8), opened by the page header's *Lock page* action: the note, the readers warning, an
 * optional reason and the Lock button. An unlocked page shows no lock card; the card (`LockPanel`) appears once the page is
 * locked. The SPA cannot evaluate `ark.locks/create` itself, so a 403 is explained in place instead.
 */
export function LockPageDrawer(props: ILockPageDrawerProps) {
    const service = props.service ?? lockService();
    const { spaceId, pageId, onClose, onLocked, onHidden } = props;
    const [reason, setReason] = useState("");
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState<string>();

    const close = useCallback(() => {
        if (isBusy) return;
        setError(undefined);
        setReason("");
        onClose();
    }, [isBusy, onClose]);

    const lock = useCallback(() => {
        setIsBusy(true);
        setError(undefined);
        service.lock(spaceId, pageId, reason).then(
            () =>
                // re-read the lock: a 404 now means the new lock hid the page from its creator
                service.getLock(spaceId, pageId).then(
                    () => {
                        setIsBusy(false);
                        setReason("");
                        onLocked();
                    },
                    (err) => {
                        setIsBusy(false);
                        setReason("");
                        if (lockErrorKind(err) === "not-found") onHidden();
                        else onLocked();
                    }
                ),
            (err) => {
                const kind = lockErrorKind(err);
                setError(kind === "forbidden" ? CannotLock : kind === "identity-unavailable" ? IdentityUnavailableText : lockErrorMessage(err));
                setIsBusy(false);
            }
        );
    }, [service, spaceId, pageId, reason, onLocked, onHidden]);

    return (
        <Drawer
            open={props.open}
            onClose={close}
            eyebrow="Page lock"
            title="Lock this page"
            lead={LockNote}
            footer={
                <>
                    <Button text="Cancel" type="secondary" onClick={close} isDisabled={isBusy} />
                    <Button text="Lock" type="primary" icon={<Icons.lock />} onClick={lock} isBusy={isBusy} />
                </>
            }
        >
            <Callout tone="warning">{LockWarning}</Callout>
            <Field label="Reason (optional)">
                <TextArea value={reason} onChange={setReason} rows={3} maxLength={1000} disabled={isBusy}
                    placeholder="Why the page is locked, for its readers" />
            </Field>
            {error ? <Callout tone="danger">{error}</Callout> : null}
        </Drawer>
    );
}
